import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ArrowRight,
  MessageCircle,
  Menu,
  X,
  Star,
  Search,
  Filter,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/motion/Reveal";
import { TESTIMONIALS } from "@/components/Testimonials";
import logoAsset from "@/assets/flashweb-logo.webp.asset.json";

const WA_URL =
  "https://wa.me/41782369200?text=Ciao%20Filippo%2C%20vorrei%20diventare%20il%20prossimo%20cliente%20FlashWeb.";

// ─── Language detection (rough, based on role + text tokens) ───
type Lang = "IT" | "EN" | "DE" | "FR";

function detectLang(text: string): Lang {
  const t = text.toLowerCase();
  if (/\b(the|was|delivered|indeed|before|flawlessly|everything|time|every)\b/.test(t))
    return "EN";
  if (/\b(und|die|der|das|schnell|zuverlässig|nächsten|abend|bestellt|unglaublich|genau)\b/.test(t))
    return "DE";
  if (/\b(j'ai|le|impeccable|dimanche|vendredi|était|commandé|service)\b/.test(t))
    return "FR";
  return "IT";
}

// ─── Sector detection based on role/company ───
type Sector =
  | "Ristorazione"
  | "Retail & Moda"
  | "Professionisti"
  | "Tech & Startup"
  | "Hospitality & Turismo"
  | "Salute & Wellness"
  | "Artigiani & Edilizia"
  | "Servizi B2B";

function detectSector(role: string): Sector {
  const r = role.toLowerCase();
  if (/(ristorante|pizzeria|chef|caff|torrefazione|foodie|restaurant|napoli)/.test(r))
    return "Ristorazione";
  if (/(retail|shop|fashion|moda|stylist|babyshop|luxe|tessile|modamilano|fashionmilano|fashionzürich|creativelab)/.test(r))
    return "Retail & Moda";
  if (/(hotel|b&b|lago|events|event planner|travel|viaggi|aviationch|pilot)/.test(r))
    return "Hospitality & Turismo";
  if (/(dentist|pharmacist|farmacia|therapist|nutritionist|dietgeneva|psychologist|mindbern|smilezürich|wellness|coach|yoga|fit|trainer|performance)/.test(r))
    return "Salute & Wellness";
  if (/(edile|contractor|carpenter|mechanic|farmer|holzzürich|autoticino|bioagricoltura|architect|architektur|edilizia)/.test(r))
    return "Artigiani & Edilizia";
  if (/(tech|code|developer|engineer|cto|startup|dataflow|techlugano|codezürich|meccanica|basel precision)/.test(r))
    return "Tech & Startup";
  if (/(lawyer|studio legale|consulting|consultant|trader|broker|finance|cfo|realtor|immobiliare|logistics|pharmabasel|design|photographer|fotografo|art director|blogger|artist|atelier)/.test(r))
    return "Professionisti";
  return "Servizi B2B";
}

const SECTORS: Sector[] = [
  "Ristorazione",
  "Retail & Moda",
  "Professionisti",
  "Tech & Startup",
  "Hospitality & Turismo",
  "Salute & Wellness",
  "Artigiani & Edilizia",
  "Servizi B2B",
];

const LANGS: Lang[] = ["IT", "EN", "DE", "FR"];

type EnrichedReview = {
  name: string;
  role: string;
  text: string;
  sector: Sector;
  lang: Lang;
};

const REVIEWS: EnrichedReview[] = TESTIMONIALS.map((t) => ({
  ...t,
  sector: detectSector(t.role),
  lang: detectLang(t.text),
}));

// ═══════════════ ROUTE ═══════════════
export const Route = createFileRoute("/recensioni")({
  head: () => ({
    meta: [
      { title: "Recensioni — 60+ aziende, 4.9/5 | FlashWeb Agency" },
      {
        name: "description",
        content:
          "Tutte le recensioni reali dei clienti FlashWeb: agenzie, ristoranti, studi professionali e retail in Svizzera e Italia.",
      },
      { property: "og:title", content: "Recensioni — 60+ aziende, 4.9/5 | FlashWeb Agency" },
      {
        property: "og:description",
        content:
          "Tutte le recensioni reali dei clienti FlashWeb in Svizzera e Italia. Valutazione media 4.9/5 su Google e Trustpilot.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.flash-web.ch/recensioni" },
    ],
    links: [{ rel: "canonical", href: "https://www.flash-web.ch/recensioni" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "FlashWeb Agency",
          url: "https://www.flash-web.ch",
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            reviewCount: String(TESTIMONIALS.length),
            bestRating: "5",
            worstRating: "1",
          },
          review: REVIEWS.slice(0, 20).map((r) => ({
            "@type": "Review",
            reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
            author: { "@type": "Person", name: r.name },
            reviewBody: r.text,
          })),
        }),
      },
          {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.flash-web.ch/" },
            { "@type": "ListItem", position: 2, name: "Recensioni", item: "https://www.flash-web.ch/recensioni" },
          ],
        }),
      },
],
  }),
  component: RecensioniPage,
});

// ─── Navbar (matches other pages) ───
function Navbar() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-border/50 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-2 group">
          <img
            src={logoAsset.url}
            alt="FlashWeb Agency"
            width="144"
            height="36"
            className="h-9 w-auto brightness-110 contrast-125 transition-transform group-hover:scale-105"
          />
          <span className="font-display text-lg font-bold tracking-tight">FlashWeb</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="hero" size="default" asChild className="hidden sm:inline-flex fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight />
            </a>
          </Button>
          <Button variant="hero" size="sm" asChild className="sm:hidden fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <button
            type="button"
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border/60 text-foreground"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-border/50 bg-background/95 backdrop-blur-xl">
          <div className="mx-auto flex max-w-6xl flex-col px-4 py-3 text-sm">
            {[
              { href: "/", label: "Home" },
              { href: "/servizi", label: "Servizi" },
              { href: "/prezzi", label: "Prezzi" },
              { href: "/come-funziona", label: "Come funziona" },
              { href: "/portfolio", label: "Portfolio" },
              { href: "/recensioni", label: "Recensioni" },
              { href: "/trasparenza", label: "Trasparenza" },
              { href: "/chi-siamo", label: "Chi siamo" },
              { href: "/faq", label: "FAQ" },
              { href: "/blog", label: "Blog" },
              { href: "/contatti", label: "Contatti" },
              { href: "/portal", label: "Area clienti" },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-foreground/90 transition-colors hover:bg-muted hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border bg-card/30">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <img src={logoAsset.url} alt="FlashWeb Agency" width="112" height="28" className="h-7 w-auto" />
              <span className="font-display text-sm font-bold">FlashWeb Agency</span>
            </div>
            <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} — Tutti i diritti riservati.</p>
            <p className="text-sm text-muted-foreground">
              Un brand di <span className="font-semibold text-foreground">FVT FuturaVision Technology GmbH</span>
            </p>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sedi</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><span className="font-semibold text-foreground">Bareerstrasse 75</span><br />6300 Zug, Svizzera</p>
              <p><span className="font-semibold text-foreground">Via Ai Prati 2</span><br />Bedano, Svizzera</p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Contatti</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><a href="mailto:info@flash-web.ch" className="font-semibold text-foreground hover:underline">info@flash-web.ch</a></p>
              <p><a href="https://wa.me/41782369200" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:underline">WhatsApp +41 78 236 92 00</a></p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Informazioni legali</h4>
            <p className="text-sm text-muted-foreground">N° registro commercio:<br /><span className="font-semibold text-foreground">CH-170.4.022.832-9</span></p>
            <div className="flex flex-col gap-1.5 pt-1 text-sm">
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy Policy</Link>
              <Link to="/cookie-policy" className="text-muted-foreground hover:text-foreground">Cookie Policy</Link>
              <Link to="/termini-condizioni" className="text-muted-foreground hover:text-foreground">Termini e Condizioni</Link>
              <Link to="/impressum" className="text-muted-foreground hover:text-foreground">Impressum</Link>
            </div>
          </div>
        </div>
        <nav aria-label="Naviga" className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-border/60 pt-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <Link to="/servizi" className="hover:text-foreground">Servizi</Link>
          <Link to="/prezzi" className="hover:text-foreground">Prezzi</Link>
          <Link to="/come-funziona" className="hover:text-foreground">Come funziona</Link>
          <Link to="/portfolio" className="hover:text-foreground">Portfolio</Link>
          <Link to="/recensioni" className="hover:text-foreground">Recensioni</Link>
          <Link to="/trasparenza" className="hover:text-foreground">Trasparenza</Link>
          <Link to="/chi-siamo" className="hover:text-foreground">Chi siamo</Link>
          <Link to="/faq" className="hover:text-foreground">FAQ</Link>
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <Link to="/contatti" className="hover:text-foreground">Contatti</Link>
        </nav>
        <div className="mt-6 flex flex-col gap-2 pt-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} FVT FuturaVision Technology GmbH — Tutti i diritti riservati.</p>
          <p>Realizzato con precisione svizzera 🇨🇭</p>
        </div>
      </div>
    </footer>
  );
}

// ═══════════════ PAGE ═══════════════
function RecensioniPage() {
  const [sector, setSector] = useState<Sector | "all">("all");
  const [lang, setLang] = useState<Lang | "all">("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return REVIEWS.filter((r) => {
      if (sector !== "all" && r.sector !== sector) return false;
      if (lang !== "all" && r.lang !== lang) return false;
      if (
        q &&
        !r.name.toLowerCase().includes(q) &&
        !r.role.toLowerCase().includes(q) &&
        !r.text.toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [sector, lang, query]);

  const sectorCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of REVIEWS) map[r.sector] = (map[r.sector] ?? 0) + 1;
    return map;
  }, []);

  const langCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of REVIEWS) map[r.lang] = (map[r.lang] ?? 0) + 1;
    return map;
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
        <div className="absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-3xl px-6 py-20 text-center sm:py-28">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-sm font-semibold text-primary backdrop-blur">
              <span className="text-[#FBBC05]">★★★★★</span>
              <span>4.9 / 5 · valutazione media</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
              Scelti da oltre <span className="text-gradient">60 aziende.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
              Ristoranti, studi professionali, retail e team tech in Svizzera e Italia.
              Ecco le parole di chi ha lanciato con FlashWeb.
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <a
                href="https://www.google.com/search?q=FlashWeb+recensioni"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 rounded-full border border-border bg-card/60 px-3 py-1.5 text-xs backdrop-blur transition-colors hover:border-primary/40"
              >
                <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden>
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                </svg>
                <span className="font-semibold text-foreground">Google</span>
                <span className="text-muted-foreground">4.9</span>
                <ExternalLink className="h-3 w-3 text-muted-foreground" />
              </a>
              <a
                href="https://www.trustpilot.com/review/flash-web.ch"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 rounded-full border border-border bg-card/60 px-3 py-1.5 text-xs backdrop-blur transition-colors hover:border-primary/40"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
                  <path fill="#00B67A" d="M12 .587l2.938 8.995H24l-7.53 5.472 2.938 8.995L12 18.577l-7.408 5.472 2.938-8.995L0 9.582h9.062z" />
                </svg>
                <span className="font-semibold text-foreground">Trustpilot</span>
                <span className="text-muted-foreground">4.9</span>
                <ExternalLink className="h-3 w-3 text-muted-foreground" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="border-b border-border bg-card/30">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 px-6 py-10 sm:grid-cols-4">
          {[
            { n: `${TESTIMONIALS.length}+`, l: "Aziende servite" },
            { n: "4.9/5", l: "Valutazione media" },
            { n: "48h", l: "Consegna media" },
            { n: "4", l: "Lingue supportate" },
          ].map((s) => (
            <div key={s.l} className="text-center">
              <div className="font-display text-3xl font-bold text-gradient sm:text-4xl">{s.n}</div>
              <div className="mt-1 text-xs uppercase tracking-widest text-muted-foreground sm:text-sm">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FILTERS */}
      <section className="mx-auto w-full max-w-6xl px-6 py-10 sm:py-14">
        <div className="mb-6 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span>Filtra le recensioni</span>
        </div>

        {/* Sector chips */}
        <div className="mb-4">
          <p className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">Settore</p>
          <div className="flex flex-wrap gap-2">
            <FilterChip
              active={sector === "all"}
              onClick={() => setSector("all")}
              label={`Tutti (${REVIEWS.length})`}
            />
            {SECTORS.map((s) => {
              const count = sectorCounts[s] ?? 0;
              if (count === 0) return null;
              return (
                <FilterChip
                  key={s}
                  active={sector === s}
                  onClick={() => setSector(s)}
                  label={`${s} (${count})`}
                />
              );
            })}
          </div>
        </div>

        {/* Lang chips */}
        <div className="mb-4">
          <p className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">Lingua</p>
          <div className="flex flex-wrap gap-2">
            <FilterChip
              active={lang === "all"}
              onClick={() => setLang("all")}
              label="Tutte"
            />
            {LANGS.map((l) => {
              const count = langCounts[l] ?? 0;
              if (count === 0) return null;
              return (
                <FilterChip
                  key={l}
                  active={lang === l}
                  onClick={() => setLang(l)}
                  label={`${l} (${count})`}
                />
              );
            })}
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome, azienda o parola chiave…"
            className="h-11 w-full rounded-2xl border border-border bg-card/60 pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground backdrop-blur transition-colors focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/30"
            aria-label="Cerca nelle recensioni"
          />
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          {filtered.length} recensione{filtered.length === 1 ? "" : "e"}
          {sector !== "all" ? ` in "${sector}"` : ""}
          {lang !== "all" ? ` in ${lang}` : ""}.
        </p>
      </section>

      {/* GRID */}
      <section className="mx-auto w-full max-w-6xl px-6 pb-16">
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card/50 p-10 text-center text-muted-foreground">
            Nessuna recensione trovata con questi filtri.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((t, i) => (
              <Reveal key={`${t.name}-${i}`} delay={Math.min(i, 8) * 0.03}>
                <div className="h-full rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary/40">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-primary">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className="h-3.5 w-3.5 fill-current" />
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="rounded-full border border-border/60 bg-background/50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                        {t.lang}
                      </span>
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                      {t.name.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <div className="text-xs">
                      <p className="font-semibold text-foreground">{t.name}</p>
                      <p className="text-muted-foreground">{t.role}</p>
                    </div>
                  </div>
                  <div className="mt-4 border-t border-border/60 pt-3">
                    <span className="text-[11px] font-medium uppercase tracking-widest text-primary/80">
                      {t.sector}
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* EXTERNAL PROOF */}
      <section className="border-y border-border bg-card/30">
        <div className="mx-auto max-w-4xl px-6 py-16 text-center sm:py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              E altre 50+ aziende ci hanno dato 5 stelle
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Le recensioni verificate sono su <span className="text-gradient">Google</span> e{" "}
              <span className="text-gradient">Trustpilot</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Preferiamo linkare le fonti reali invece di riportare qui testimonianze non
              verificabili. Clicca per leggere le recensioni originali dei nostri clienti.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button variant="outlineHero" size="lg" asChild>
                <a
                  href="https://www.google.com/search?q=FlashWeb+recensioni"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Recensioni Google <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
              <Button variant="outlineHero" size="lg" asChild>
                <a
                  href="https://www.trustpilot.com/review/flash-web.ch"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Recensioni Trustpilot <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative">
        <div className="pointer-events-none absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-3xl px-6 py-20 text-center sm:py-24">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary backdrop-blur">
              <MessageCircle className="h-3 w-3" />
              Vuoi essere il prossimo?
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              La tua recensione sarà la <span className="text-gradient">prossima</span> su questa pagina.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Scrivici su WhatsApp: risposta in giornata, preventivo chiaro entro 24 ore,
              sito online in 48 ore lavorative dal briefing completo.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button variant="hero" size="xl" asChild className="fw-shimmer">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-5 w-5" /> Inizia su WhatsApp
                </a>
              </Button>
              <Button variant="outlineHero" size="xl" asChild>
                <Link to="/portfolio">
                  Vedi il portfolio <ArrowRight />
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
        active
          ? "border-primary/60 bg-primary/15 text-primary shadow-[0_0_20px_hsl(var(--primary)/0.15)]"
          : "border-border bg-card/50 text-muted-foreground hover:border-primary/30 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}
